import { QdsChoiceBase } from '../shared/choice-base.js';
import type { QdsOption } from '../../types/index';

/**
 * Multiple independent checkbox choices with one repeated form field and validation lifecycle.
 * Native invalid reporting focuses the visible control while preserving labels and cancelable invalid events.
 * @element qds-checkbox-group
 * @attr {string} label - Visible accessible group name.
 * @attr {string} aria-label - Explicit accessible group name.
 * @attr {string} aria-labelledby - External group label IDs.
 * @attr {string} name - Repeated native form field name.
 * @attr {string} form - Associated form ID.
 * @attr {string} hint - Group instructions.
 * @attr {string} error-text - Constraint/author error message.
 * @attr {boolean} required - At least one enabled available choice must be selected.
 * @attr {boolean} invalid - Author-supplied invalid state.
 * @attr {boolean} disabled - Disable the entire group and omit form values.
 * @attr {boolean} readonly - Keep values and focus while preventing user changes.
 * @attr {'blur'|'input'|'submit'} validate-on - Error timing; default blur.
 * @attr {'sm'|'md'|'lg'} size - Label/target size; all hit targets remain at least 44px.
 * @property {ReadonlyArray<{value: string; label: string; disabled?: boolean; description?: string}>} options - Cloned typed checkbox choices.
 * @property {string[]} values - Selected values in option order; programmatic assignment is silent.
 * @property {string[]} defaultValues - Reset selection.
 * @property {boolean} [readonly] multiple - Always true. This group never switches to scalar/radio selection.
 * @fires {{component: HTMLElement; value: string; values: string[]; multiple: boolean}} input - User changes a checkbox.
 * @fires {{component: HTMLElement; value: string; values: string[]; multiple: boolean}} change - User commits a checkbox change.
 * @fires {{component: HTMLElement; valid: boolean; validity: {valid: boolean; valueMissing: boolean; customError: boolean}; message: string}} qds-validity-change - Validation transition.
 * @csspart label - Visible group label.
 * @csspart options - Named checkbox group.
 * @csspart option - Native button with checkbox semantics and a visible checked mark.
 * @csspart hint - Helper text.
 * @csspart error - Validation feedback.
 */
export class QdsCheckboxGroup extends QdsChoiceBase {
  override get multiple():boolean{return true;}
  protected get _choiceStyles():string{return `
    :host {display:block;}
    [part=options] {display:flex;flex-direction:column;gap:.25rem;}
    [part=option] {display:flex;align-items:center;gap:.625rem;min-height:44px;padding:.5rem;text-align:start;cursor:pointer;
      font:inherit;color:var(--theme-text-primary,#333);background:var(--theme-background-primary,white);border:1px solid transparent;border-radius:4px;}
    .mark {display:inline-flex;align-items:center;justify-content:center;flex:0 0 20px;width:20px;height:20px;border:1px solid var(--theme-text-secondary,#555);border-radius:3px;}
    [aria-checked=true] .mark {background:var(--theme-accent-primary,#445e89);color:var(--theme-text-on-accent-primary,white);border-color:var(--theme-accent-primary,#445e89);}
    [part=option]:hover:not(:disabled) {background:var(--theme-background-hover,#f0f0f0);}
    [part=option]:focus-visible {outline:2px solid var(--theme-accent-primary,#445e89);outline-offset:2px;}
    [part=option]:disabled {cursor:not-allowed;opacity:.6;}
    .copy {display:flex;flex-direction:column;}
    .description {font-size:.875em;}
    :host([size=sm]) [part=option] {font-size:.8125rem;}
    :host([size=lg]) [part=option] {min-height:48px;font-size:1rem;}
    @media(forced-colors:active){.mark{border-color:ButtonText;}[aria-checked=true] .mark{background:Highlight;color:HighlightText;border-color:Highlight;}}
  `;}
  protected override _createOption(option:QdsOption):HTMLButtonElement{
    const button=super._createOption(option);button.textContent='';
    const mark=document.createElement('span'),copy=document.createElement('span'),label=document.createElement('span');
    mark.className='mark';mark.setAttribute('aria-hidden','true');copy.className='copy';label.textContent=option.label;copy.append(label);
    if(option.description){const description=document.createElement('span');description.className='description';description.textContent=option.description;copy.append(description);}
    button.append(mark,copy);return button;
  }
  protected _syncChoices():void{
    this._control!.setAttribute('role','group');this._control!.setAttribute('aria-disabled',String(this.disabled));
    this._control!.setAttribute('aria-description',this.required?'Choose at least one option.':'Choose any applicable options.');
    const selected=new Set(this.values);
    for(const [index,button]of this._buttons().entries()){
      const checked=selected.has(button.dataset.choice!);button.setAttribute('role','checkbox');button.setAttribute('aria-checked',String(checked));button.setAttribute('aria-readonly',String(this.readonly));
      button.disabled=this.disabled||!!this._options[index].disabled;button.tabIndex=button.disabled?-1:0;button.querySelector('.mark')!.textContent=checked?'✓':'';
    }
  }
  protected _bind():void{/* Native button Space/Enter activation and independent Tab order. */}
  protected _unbind():void{/* All listeners belong to the shared form lifecycle. */}
}
if(!customElements.get('qds-checkbox-group'))customElements.define('qds-checkbox-group',QdsCheckboxGroup);
